import { useEffect, useState } from 'react';
import { Autocomplete, Box, CircularProgress, TextField, Typography } from '@mui/material';
import {
  contractorLabel,
  searchContractors,
  toErrorMessage,
  type ContractorRef,
} from './preOrderContractorApi';

/**
 * Wyszukiwarka kontrahenta z kartoteki (makieta back office v2, fala B,
 * 21.10.2026; S-21.10-10; BR-ADM-13: „system podpowiada, ale przy wielu
 * oddziałach pod jednym NIP wybiera użytkownik”).
 *
 * Szuka po nazwie, NIP i mieście, a sama liczba — po numerze klienta w TMS.
 * Podpowiedź: dopóki operator nic nie wpisał, lista zaczyna od nazwy klienta
 * z prezlecenia — ale wybiera człowiek; nazwy na kontrahenta nie zamieniamy
 * sami (dopasowanie po nazwie to zgadywanie). Każda pozycja niesie NIP,
 * numer klienta w TMS i miejscowość, żeby odróżnić oddziały.
 */
export const ContractorPicker = ({
  value,
  onChange,
  suggestFrom,
  disabled = false,
  debounceMs = 300,
}: {
  value: ContractorRef | null;
  onChange: (c: ContractorRef | null) => void;
  /** Nazwa klienta z prezlecenia — od niej startuje lista podpowiedzi. */
  suggestFrom?: string | null;
  disabled?: boolean;
  debounceMs?: number;
}) => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [options, setOptions] = useState<ContractorRef[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Wpisany tekst ma pierwszeństwo; bez niego — nazwa klienta z prezlecenia.
  const typed = value && input === contractorLabel(value) ? '' : input;
  const query = typed.trim() !== '' ? typed : (suggestFrom ?? '');
  const suggesting = typed.trim() === '' && (suggestFrom ?? '').trim() !== '';

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    const timer = setTimeout(() => {
      setLoading(true);
      searchContractors(query)
        .then((found) => {
          if (!active) return;
          setOptions(found);
          setError(null);
        })
        .catch((e: unknown) => {
          if (active) setError(toErrorMessage(e));
        })
        .finally(() => {
          if (active) setLoading(false);
        });
    }, debounceMs);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [open, query, debounceMs]);

  return (
    <Box data-testid="contractor-picker">
      <Autocomplete<ContractorRef>
        size="small"
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        value={value}
        disabled={disabled}
        options={value && !options.some((o) => o.id === value.id) ? [value, ...options] : options}
        // Wyniki filtruje API — lista pokazuje je tak, jak przyszły.
        filterOptions={(x) => x}
        loading={loading}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        getOptionLabel={(o) => contractorLabel(o)}
        onChange={(_e, c) => onChange(c)}
        inputValue={input}
        onInputChange={(_e, v) => setInput(v)}
        noOptionsText={
          query.trim() === ''
            ? 'Wpisz nazwę, NIP albo numer klienta w TMS'
            : 'Brak w kartotece — klient spoza kartoteki zostaje nazwą tekstem'
        }
        loadingText="Szukam w kartotece…"
        renderOption={(props, o) => {
          const { key, ...rest } = props as typeof props & { key: string };
          return (
            <li key={key} {...rest} data-testid={`contractor-option-${o.id}`}>
              <Box>
                <Typography variant="body2">{o.legalName}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {[
                    o.taxId ? `NIP ${o.taxId}` : 'bez NIP',
                    o.tmsCustomerId !== null ? `nr klienta TMS ${o.tmsCustomerId}` : 'spoza TMS',
                    [o.city, o.country].filter(Boolean).join(', ') || null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </Typography>
              </Box>
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Kontrahent z kartoteki"
            helperText={
              error ??
              (suggesting && open
                ? `Podpowiedzi dla „${(suggestFrom ?? '').trim()}” — wybierz z listy albo wpisz inaczej`
                : 'Nazwa, NIP albo numer klienta w TMS; z niego podpowiedź VAT')
            }
            error={error !== null}
            slotProps={{
              ...params.slotProps,
              htmlInput: { ...params.slotProps.htmlInput, 'data-testid': 'contractor-picker-input' },
              input: {
                ...params.slotProps.input,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress size={16} /> : null}
                    {params.slotProps.input.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
      />
    </Box>
  );
};
